<template>
  <div>
    <FilterTitle
      :name="validationTypeName"
      @show="showValidationOptions = true"
      @hide="showValidationOptions = false"
    />
    <RadioFilter
      v-if="showValidationOptions"
      class="mt-1"
      :options="validationOptions"
      :initialValue="filters.validation"
      @change="$emit('change', { filter: 'filter', value: $event.id })"
    />

    <FilterTitle
      v-if="isImplementationTab"
      class="mt-4"
      :name="$t('common.role')"
      @show="showRoleOptions = true"
      @hide="showRoleOptions = false"
    />
    <CheckboxFilter
      v-if="isImplementationTab && showRoleOptions"
      class="mt-1"
      :options="roleOptions"
      :initialValue="filters.role ? filters.role : []"
      @change="$emit('change', { filter: 'role', value: $event })"
    />

    <div data-tourid="validationsView-filterGroup-selectors">
      <MultipleSelect
        class="mt-4"
        data-testid="validations-filterSelector-site"
        persistentHint
        hideDetails
        itemValue="id"
        :maxChipsShown="2"
        :value="filters.sites"
        :options="siteOptions"
        :showSelectAll="false"
        :label="$t('common.site_one')"
        @change="$emit('change', { filter: 'sites', value: $event })"
      />

      <MultipleSelect
        class="mt-4"
        data-testid="validations-filterSelector-siteGroups"
        hideDetails
        itemValue="id"
        :value="filters.siteGroups"
        :options="organizationSiteGroups"
        :showSelectAll="false"
        :label="$t('common.site_group_other')"
        @change="$emit('change', { filter: 'siteGroups', value: $event })"
      />

      <MultipleSelect
        class="white mt-4"
        itemValue="id"
        hide-details
        :loading="isLoadingCountries"
        :maxChipsShown="2"
        :value="filters.countryCodes"
        :options="countryCodeOptions"
        :showSelectAll="false"
        :label="$t('common.country_other')"
        @change="$emit('change', { filter: 'countryCodes', value: $event })"
      />

      <ExploreTagsSelector
        data-tourid="validations-tags-filter"
        data-testid="validations-filterSelector-tags"
        class="mt-4"
        :value="filters.tags"
        @change="$emit('change', { filter: 'tags', value: $event })"
      />

      <MultipleSelect
        data-testid="validations-filterSelector-tagsGroups"
        class="mt-4"
        hideDetails
        itemValue="id"
        :value="filters.tagGroups"
        :options="organizationTagGroups"
        :label="$t('organization_structure.tags_group_other')"
        @change="$emit('change', { filter: 'tagGroups', value: $event })"
      />

      <MultipleSelect
        class="mt-4"
        data-testid="validations-filterSelector-categories"
        persistentHint
        hideDetails
        itemValue="id"
        :maxChipsShown="2"
        :value="filters.categories"
        :options="categories"
        :showSelectAll="false"
        :label="$t('common.category_one')"
        @change="$emit('change', { filter: 'categories', value: $event })"
      />
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

import api from '@/api'

import FilterTitle from '@/components/Filters/FilterTitle.vue'
import RadioFilter from '@/components/Filters/RadioFilter.vue'
import MultipleSelect from '@/components/Core/MultipleSelect.vue'
import ExploreTagsSelector from '../Explore/ExploreTagsSelector.vue'

import mixin from '@/mixins/mixin.vue'
import CheckboxFilter from '../../components/Filters/CheckboxFilter.vue'

export default {
  name: 'ValidationsRequestFilters',

  mixins: [mixin],

  components: {
    FilterTitle,
    RadioFilter,
    MultipleSelect,
    ExploreTagsSelector,
    CheckboxFilter,
  },

  props: {
    filters: { type: Object, default: () => {} },
    isImplementationTab: Boolean,
  },

  data() {
    return {
      showValidationOptions: true,
      showRoleOptions: true,
      categories: [],
      isLoadingCountries: false,
      countryCodes: [],
    }
  },

  mounted() {
    this.getCategories()
    this.fetchCountries()
  },

  computed: {
    ...mapGetters([
      'organizationId',
      'organizationSites',
      'organizationSiteGroups',
      'organizationTags',
      'organizationTagGroups',
    ]),

    siteOptions() {
      return this.organizationSites.map(site => {
        return {
          ...site,
          subtitle: this.$t(`countries.${site.countryCodes[0]}`),
          subtitleIcon: 'mdi-earth',
        }
      })
    },

    countryCodeOptions() {
      return (
        this.$constants.countries
          .map?.(country => {
            return {
              ...country,
              id: country.code,
              name: this.$t(`countries.${country.code}`),
            }
          })
          .filter?.(country =>
            this.countryCodes?.some?.(
              countryCode => countryCode === country.code
            )
          ) ?? []
      )
    },

    validationTypeName() {
      return this.filters.activeTab ===
        this.$constants.validationsFilters.contentTab.impact
        ? this.$t('common.impact')
        : this.$t('common.implementation')
    },

    validationOptions() {
      return _.compact([
        !this.isImplementationTab && {
          id: this.$constants.validationsFilters.validations.assignedToMe,
          text: this.$t('rev.validation.assigned_to_me'),
          testId: 'validations-filter-assigned-to-me',
          tourId: 'validationsView-filter-assignedToMe',
        },
        {
          id: this.$constants.validationsFilters.validations.pending,
          text: this.isImplementationTab
            ? this.$t('rev.validation.not_implemented')
            : this.$t('rev.validation.impact_not_validated'),
          testId: 'validations-filter-pending',
          tourId: this.isImplementationTab
            ? 'validationsView-filter-notImplemented'
            : 'validationsView-filter-notValidated',
        },
        {
          id: this.$constants.validationsFilters.validations.validated,
          text: this.isImplementationTab
            ? this.$t('rev.validation.implemented')
            : this.$t('rev.validation.impact_validated'),
          testId: 'validations-filter-pending',
          tourId: this.isImplementationTab
            ? 'validationsView-filter-implemented'
            : 'validationsView-filter-validated',
        },
      ])
    },

    roleOptions() {
      return _.compact([
        this.filters.activeTab ===
          this.$constants.validationsFilters.contentTab.implementation && {
          id: 'coach',
          text: this.$t('common.roles.i_am_coach'),
          testId: 'roles_filter_coach',
          tourId: 'validationsView-filter-coachRole',
        },
      ])
    },
  },

  methods: {
    async fetchCountries() {
      try {
        this.isLoadingCountries = true
        const response = await api.country.getAllCountryCodes(
          this.organizationId
        )

        this.countryCodes = response.data
      } catch (error) {
        this.handleRequestError(error)
      } finally {
        this.isLoadingCountries = false
      }
    },

    async getCategories() {
      try {
        const response = await api.category.getOrganizationCatalog(
          this.organizationId
        )
        this.categories = response.data.map(category => {
          return {
            ...category,
            id: category.id,
            name: this.$t(`common.categories.${category.name}`),
          }
        })
      } catch (err) {
        this.showError(err)
      }
    },
  },
}
</script>
